import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import './Dealermap.css';
import importedDealerData from './Dealer.json';

const Dealermap = ({ dealerData }) => {
  const [selectedState, setSelectedState] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [filteredDealers, setFilteredDealers] = useState([]);
  const [availableDistricts, setAvailableDistricts] = useState([]);
  const [svgContent, setSvgContent] = useState(null);
  const [currentMapState, setCurrentMapState] = useState('');
  const mapRef = useRef(null);

  const dataSource = dealerData || importedDealerData;

  // stateMapImages is constant, so we can define it outside or use useMemo
  const stateMapImages = useMemo(() => ({
    'AllStates': '/maps/india.svg',
    'Maharashtra': '/maps/Maharashtra.svg',
    'Karnataka': '/maps/Karnataka.svg',
    'ANDHRA PRADESH': '/maps/AndhraPradesh.svg',
    'TELANGANA': '/maps/Telangana.svg',
    'ODISHA': '/maps/Odisha.svg'
  }), []);

  const getAllDealers = useCallback(() => {
    if (!dataSource || typeof dataSource !== 'object') {
      return [];
    }
    
    const allDealers = [];
    Object.keys(dataSource).forEach(districtKey => {
      const dealersArray = dataSource[districtKey];
      
      if (Array.isArray(dealersArray)) {
        dealersArray.forEach(dealer => {
          if (dealer['Dealer Name'] && dealer['Dealer Name'].trim()) {
            allDealers.push({
              name: dealer['Dealer Name'].trim(),
              address: dealer['Address'] || '',
              district: dealer['District'] || '',
              state: dealer['State'] || ''
            });
          }
        });
      }
    });
    
    return allDealers;
  }, [dataSource]);

  const allDealers = useMemo(() => getAllDealers(), [getAllDealers]);
  const states = useMemo(() => 
    [...new Set(allDealers.map(dealer => dealer.state))].filter(Boolean),
    [allDealers]
  );

  const getMapKey = useCallback(() => {
    if (selectedState && stateMapImages[selectedState]) {
      return selectedState;
    }
    return 'AllStates';
  }, [selectedState, stateMapImages]);

  useEffect(() => {
    const mapKey = getMapKey();
    
    if (mapKey !== currentMapState) {
      const loadSVG = async () => {
        const mapUrl = stateMapImages[mapKey] || '/maps/india.svg';
        try {
          const response = await fetch(mapUrl);
          let svgText = await response.text();
          
          const parser = new DOMParser();
          const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');
          const svgElement = svgDoc.querySelector('svg');
          
          if (svgElement) {
            svgElement.removeAttribute('width');
            svgElement.removeAttribute('height');
            
            if (!svgElement.getAttribute('viewBox')) {
              const width = svgElement.getAttribute('width') || '800';
              const height = svgElement.getAttribute('height') || '800';
              svgElement.setAttribute('viewBox', `0 0 ${width} ${height}`);
            }
            
            svgElement.setAttribute('preserveAspectRatio', 'xMidYMid meet');
            svgElement.setAttribute('style', 'width: 100%; height: 100%;');
            
            svgText = new XMLSerializer().serializeToString(svgDoc);
          }
          
          setSvgContent(svgText);
          setCurrentMapState(mapKey);
        } catch (error) {
          console.error('Error loading SVG:', error);
          setSvgContent(null);
        }
      };

      loadSVG();
    }
  }, [selectedState, currentMapState, getMapKey, stateMapImages]);

  useEffect(() => {
    if (selectedState) {
      const districts = [...new Set(
        allDealers
          .filter(dealer => dealer.state === selectedState)
          .map(dealer => dealer.district)
      )].filter(Boolean).sort();
      setAvailableDistricts(districts);
    } else {
      setAvailableDistricts([]);
    }
    
    if (selectedDistrict && selectedState) {
      const districts = allDealers
        .filter(dealer => dealer.state === selectedState)
        .map(dealer => dealer.district);
      if (!districts.includes(selectedDistrict)) {
        setSelectedDistrict('');
      }
    } else if (!selectedState) {
      setSelectedDistrict('');
    }
  }, [selectedState, allDealers, selectedDistrict]);

  useEffect(() => {
    let filtered = allDealers;
    
    if (selectedState) {
      filtered = filtered.filter(dealer => dealer.state === selectedState);
    }
    
    if (selectedDistrict) {
      filtered = filtered.filter(dealer => dealer.district === selectedDistrict);
    }
    
    setFilteredDealers(filtered);
  }, [selectedState, selectedDistrict, allDealers]);

  useEffect(() => {
    const mapContainer = mapRef.current;
    if (!mapContainer || !svgContent) return;

    const handleMapClick = (e) => {
      let target = e.target;
      let districtName = null;
      
      for (let i = 0; i < 5 && target; i++) {
        districtName = target.getAttribute('data-district');
        if (districtName) {
          console.log('Clicked district:', districtName);
          console.log('Available districts:', availableDistricts);
          break;
        }
        target = target.parentElement;
      }

      if (districtName) {
        if (!selectedState) {
          const dealer = allDealers.find(d => d.district === districtName);
          if (dealer) {
            setSelectedState(dealer.state);
            setTimeout(() => setSelectedDistrict(districtName), 100);
          }
        } else {
          if (availableDistricts.includes(districtName)) {
            setSelectedDistrict(districtName);
          } else {
            console.warn(`District "${districtName}" not found in available districts`);
          }
        }
      }
    };

    const handleMouseOver = (e) => {
      let target = e.target;
      for (let i = 0; i < 5 && target; i++) {
        if (target.getAttribute('data-district')) {
          target.style.opacity = '0.7';
          target.style.cursor = 'pointer';
          target.style.transition = 'opacity 0.2s';
          break;
        }
        target = target.parentElement;
      }
    };

    const handleMouseOut = (e) => {
      let target = e.target;
      for (let i = 0; i < 5 && target; i++) {
        if (target.getAttribute('data-district')) {
          target.style.opacity = '1';
          break;
        }
        target = target.parentElement;
      }
    };

    mapContainer.addEventListener('click', handleMapClick);
    mapContainer.addEventListener('mouseover', handleMouseOver);
    mapContainer.addEventListener('mouseout', handleMouseOut);

    return () => {
      mapContainer.removeEventListener('click', handleMapClick);
      mapContainer.removeEventListener('mouseover', handleMouseOver);
      mapContainer.removeEventListener('mouseout', handleMouseOut);
    };
  }, [availableDistricts, svgContent, selectedState, allDealers]);

  const handleStateFilterChange = (e) => {
    setSelectedState(e.target.value);
  };

  const handleDistrictFilterChange = (e) => {
    setSelectedDistrict(e.target.value);
  };

  const clearFilters = () => {
    setSelectedState('');
    setSelectedDistrict('');
  };

  return (
    <div className="dealermap-container">
      <div className="dealermap-content">
        <div className="dealermap-map-section">
          <h2 className="dealermap-map-title">
            {selectedState ? `${selectedState} Map` : 'India Map'}
          </h2>
          <div className="dealermap-map-container">
            <div 
              className="dealermap-image-map-wrapper" 
              ref={mapRef}
              dangerouslySetInnerHTML={{ __html: svgContent || '' }}
            />
          </div>
        </div>

        <div className="dealermap-filter-section">
          <div className="dealermap-filters">
            <div className="dealermap-filter-row">
              <div className="dealermap-filter-group">
                <label htmlFor="state-filter" className="dealermap-filter-label">Filter by State</label>
                <select 
                  id="state-filter"
                  className="dealermap-filter-select"
                  value={selectedState}
                  onChange={handleStateFilterChange}
                >
                  <option value="">All States</option>
                  {states.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              
              <div className="dealermap-filter-group">
                <label htmlFor="district-filter" className="dealermap-filter-label">Filter by District</label>
                <select 
                  id="district-filter"
                  className="dealermap-filter-select"
                  value={selectedDistrict}
                  onChange={handleDistrictFilterChange}
                  disabled={!selectedState}
                >
                  <option value="">All Districts</option>
                  {availableDistricts.map(district => (
                    <option key={district} value={district}>{district}</option>
                  ))}
                </select>
              </div>
              
              <button 
                className="dealermap-clear-btn"
                onClick={clearFilters}
              >
                Clear All
              </button>
            </div>
          </div>

          <div className="dealermap-table-container">
            <table className="dealermap-table">
              <thead>
                <tr>
                  <th className="dealermap-th-name">Dealer Name</th>
                  <th className="dealermap-th-address">Address</th>
                  <th className="dealermap-th-district">District</th>
                  <th className="dealermap-th-state">State</th>
                </tr>
              </thead>
              <tbody>
                {filteredDealers.length > 0 ? (
                  filteredDealers.map((dealer, index) => (
                    <tr key={index} className="dealermap-table-row">
                      <td className="dealermap-name-cell">{dealer.name}</td>
                      <td className="dealermap-address-cell">{dealer.address}</td>
                      <td className="dealermap-district-cell">{dealer.district}</td>
                      <td className="dealermap-state-cell">{dealer.state}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="dealermap-no-data">
                      {selectedState || selectedDistrict ? 'No dealers found for the selected filters' : 'Select a state from the filter to view dealers'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dealermap;